import { useDreAuditList, type AuditStatus, type DreAuditListItem } from '../../hooks/useDreAudit'
import { Upload, Edit3, ChevronRight, AlertCircle, CheckCircle2, Clock } from 'lucide-react'
import { formatPeriodShort } from './diffUtils'
import { formatDateBR } from '../../utils/dateUtils'

interface AuditHistoryListProps {
  onSelect: (id: string) => void
}

export function AuditHistoryList({ onSelect }: AuditHistoryListProps) {
  const query = useDreAuditList({ limit: 30 })

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 bg-brand-dark-lighter rounded-monumenta animate-pulse" />
        ))}
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="p-4 rounded-md bg-error/10 border border-error/30 text-sm text-error">
        Erro ao carregar histórico: {(query.error as Error)?.message ?? 'erro desconhecido'}
      </div>
    )
  }

  const items = query.data?.items ?? []

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-brand-gray">
          Nenhum snapshot ainda. Snapshots são gerados automaticamente após cada importação ou edição em lote.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {items.map(item => (
        <ListRow key={item.id} item={item} onClick={() => onSelect(item.id)} />
      ))}
    </div>
  )
}

interface ListRowProps {
  item: DreAuditListItem
  onClick: () => void
}

function ListRow({ item, onClick }: ListRowProps) {
  const Icon = item.source === 'processImport' ? Upload : Edit3
  const sourceLabel = item.source === 'processImport' ? 'Importação' : 'Edição em lote'
  const description = describeTrigger(item)

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 bg-brand-dark-light rounded-monumenta border border-brand-dark-lighter hover:border-brand-primary/40 hover:bg-brand-dark-lighter/40 transition-colors text-left"
    >
      <div className="w-9 h-9 rounded-monumenta bg-brand-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm text-white">
          <span className="font-medium">{sourceLabel}</span>
          <StatusBadge status={item.status} />
        </div>
        <div className="text-xs text-brand-gray mt-0.5 truncate">
          {description}
        </div>
        <div className="text-xs text-brand-gray-light mt-0.5">
          {formatDateBR(item.createdAt)} {new Date(item.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          {item.affectedPeriods.length > 0 && (
            <span className="ml-2">
              · períodos: {item.affectedPeriods.map(p => formatPeriodShort(p)).join(', ')}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-brand-gray shrink-0" />
    </button>
  )
}

function describeTrigger(item: DreAuditListItem): string {
  if (item.source === 'processImport') {
    const parts: string[] = []
    if (item.trigger.fileName) parts.push(item.trigger.fileName)
    if (typeof item.trigger.rowCount === 'number') {
      parts.push(`${item.trigger.rowCount} linha${item.trigger.rowCount === 1 ? '' : 's'}`)
    }
    return parts.length > 0 ? parts.join(' · ') : 'Sem detalhes'
  }
  // updateMany
  const u = item.trigger.updatedCount ?? 0
  const d = item.trigger.deletedCount ?? 0
  const parts: string[] = []
  if (u > 0) parts.push(`${u} atualizada${u === 1 ? '' : 's'}`)
  if (d > 0) parts.push(`${d} excluída${d === 1 ? '' : 's'}`)
  return parts.length > 0 ? parts.join(' · ') : 'Sem alterações'
}

function StatusBadge({ status }: { status: AuditStatus }) {
  if (status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-success/15 text-success uppercase tracking-wide">
        <CheckCircle2 size={10} /> Completo
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-error/15 text-error uppercase tracking-wide">
        <AlertCircle size={10} /> Falhou
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warning/15 text-warning uppercase tracking-wide">
      <Clock size={10} /> Pendente
    </span>
  )
}
